import {useState} from 'react';
import {useLocation} from 'react-router-dom';
import {MessageCircle, Send} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogTrigger,
  FeedbackDialogContent,
  SidebarMenuButton,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {useCreateFeedback} from '@/api/hooks';
import {type CreateFeedbackRequest} from '@/api/feedback';
import {getApiErrorMessage, getLastApiError} from '@/api/client';
import {showToast} from '@/lib/toast';

function field(data: FormData, name: string): string | undefined {
  const value = data.get(name);
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

// The dialog's fields are documented on FeedbackDialogContent: `kind` always, `rating` and `detail`
// on feedback, `title`, `detail`, `url` and `impact` on a bug. Only the submitted tab's are present.
export function toFeedbackRequest(
  data: FormData,
  page: string,
  lastError: string | undefined
): CreateFeedbackRequest {
  const isBug = data.get('kind') === 'bug';
  const rating = Number(data.get('rating') ?? 0);
  return {
    kind: isBug ? 'bug' : 'feedback',
    page,
    message: field(data, 'detail'),
    last_error: lastError,
    ...(isBug
      ? {title: field(data, 'title'), url: field(data, 'url'), impact: field(data, 'impact')}
      : {rating: rating > 0 ? rating : undefined})
  };
}

function isEmpty(request: CreateFeedbackRequest): boolean {
  return (
    request.message === undefined && request.title === undefined && request.rating === undefined
  );
}

// The design system's App Feedback dialog; the app owns only the trigger and where it posts.
export function FeedbackDialog() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const {mutate, isPending} = useCreateFeedback();

  const handleSubmit = (data: FormData) => {
    if (isPending) {
      return;
    }

    // The last failed API call rides along so a "it broke" report carries the error the analyst saw.
    const request = toFeedbackRequest(data, location.pathname, getLastApiError());
    if (isEmpty(request)) {
      showToast(
        request.kind === 'bug'
          ? 'Give the bug a title or describe what happened.'
          : 'Add a rating or a comment.',
        'error'
      );
      return;
    }

    mutate(request, {
      onSuccess() {
        setOpen(false);
        showToast('Thanks - your feedback was recorded.');
      },
      onError(error) {
        showToast(getApiErrorMessage(error, 'Failed to send feedback.'), 'error');
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <SidebarMenuButton render={<DialogTrigger />} tooltip="Feedback">
        <MessageCircle />
        <span>Feedback</span>
      </SidebarMenuButton>
      {/* No uploads: a screenshot of PRI can carry the sensitive material analysts review, and
          accepting files would make PRI responsible for storing them. */}
      <FeedbackDialogContent
        hasAttachments={false}
        pageUrl={globalThis.location.href}
        footer={
          <>
            <DialogClose render={<Button variant="ghost" />}>Cancel</DialogClose>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Spinner /> : <Send />}
              {isPending ? 'Sending…' : 'Submit'}
            </Button>
          </>
        }
        onSubmit={handleSubmit}
      />
    </Dialog>
  );
}
